// Report header (DESIGN_SPEC §11.4.1, RESTYLE §4) for every variant but the live cockpit:
// .rhead = [id | lens] over [body | lens]; id → lens → body below 900px so the verdict comes first on phones.
//   <ReportHeader ctx variant facts lens actions? identity? />   identity=false: the queued view shows it in its body
//   id:   kicker "— ANALYSIS · <id> · STATE" (+ "record syncing" / "stream stopped at seq N"), the mono sample headline
//         beside its status chip (the filled verdict banner of a completed run, the dashed "· partial" banner of a run
//         that stopped early, "not scored", QUEUED, or an ink "× FAILED" / "× CANCELLED"), the threat tags of a scored
//         run (none when it stopped before the sample started), "child of …"
//   body: (an alert while the event stream of a dispatched run cannot connect and nothing arrived: retrying in N s ·
//         Retry now) · actions · lifecycle stepper (job.state events, UTC) · identity sheets · child analyses
// Also exports, for the other views:
//   <DocumentActions ctx plan? />   Download report (primary; presigned behavior-report.json, else report.html) ·
//                                   Export IOCs JSON | CSV · Artifacts N (scrolls) · Open printable report (ghost)
//   <Lifecycle ctx facts />         the stepper with its label line · scrollToFig(no) · saveBlob(name, blob) · baseName(job)
import { useMemo, useState, useSyncExternalStore, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Arrow,
  CopyButton,
  Kicker,
  Kid,
  Pip,
  QueuedBadge,
  Sep,
  Stepper,
  ThreatTags,
  Time,
  VerdictBanner,
  buttonClass,
  type Step,
} from "@/components/ui";
import { pickArtifact, useClock, usePresignedDownload } from "@/hooks";
import { api } from "@/lib/api";
import { backgroundIoc } from "@/lib/derive/noise";
import { clock, count, dur, rel, secs, utc } from "@/lib/format";
import type { JobStream, ReportCtx } from "@/lib/derive/types";
import { reportVerdict } from "@/lib/derive/verdict";
import { jobName } from "@/lib/records";
import type { IocEntry, Job } from "@/types";
import { ChildJobs, ParentLink } from "./ChildJobs";
import type { ReportFacts, Variant } from "./facts";
import type { DocumentPlan } from "./figOrder";
import { IdentitySheets } from "./IdentitySheets";
import { displayOf, lifecycle, type LifeStep } from "./lifecycle";
import { scrollToId } from "./scroll";
import { threatItems } from "./threats";

const NOTE = "normal-case tracking-[.04em] text-ink-faint";
/** A headline longer than this gets a copy button (it is clamped to two lines). */
const LONG_NAME = 48;

/** The kicker's state word; the colourful status lives beside the headline. */
function StateWord({ stream }: { stream: JobStream }) {
  const s = stream.state;
  if (s === "failed" || s === "cancelled")
    return (
      <span className="inline-flex items-center gap-2 text-ink-soft">
        <span aria-hidden="true" className="text-ink">
          ×
        </span>
        {s}
      </span>
    );
  if (s === "queued" || s === "completed") return <span>{s}</span>;
  return (
    <span className="inline-flex items-center gap-2">
      <Pip state="live" batchId={stream.batchId} />
      {displayOf(s) ?? s}
    </span>
  );
}

const STEP_LABEL: Record<LifeStep["state"], string> = {
  queued: "Queued",
  provisioning: "Provisioning",
  booting: "Booting",
  running: "Running",
  collecting: "Collecting",
  completed: "Completed",
  failed: "Failed",
  cancelled: "Cancelled",
};

export function Lifecycle({ ctx, facts, className }: { ctx: ReportCtx; facts: ReportFacts; className?: string }) {
  const { stream } = ctx;
  const now = useClock(1000, stream.live);
  // while the guest boots, T0 is provisional (it moves to RUNNING once the sample starts): tag it only when settled
  const t0Settled = stream.t0Source === "running" || !stream.live;
  const steps = lifecycle(stream.transitions, stream.state, t0Settled ? stream.t0Source : null);
  const first = steps[0]?.ts ?? null;
  const out: Step[] = steps.map((s) => {
    const step: Step = { label: STEP_LABEL[s.state], state: s.status, t0: s.t0 };
    if (s.ts !== null) step.time = <Time value={s.ts} mode="ms" />;
    else if (s.status === "done") step.time = "—";
    else if (s.status === "future" && s.state === "collecting" && facts.deadline !== null) step.time = `by ${utc(facts.deadline)}`;
    if ((s.status === "current" || s.status === "waiting") && s.ts !== null) step.duration = `${clock(Math.max(0, now - s.ts), 1)} so far`;
    else if (s.status === "done" && s.state === "completed" && s.ts !== null && first !== null && s.ts > first) step.duration = `${dur(s.ts - first)} in total`;
    else if (s.status === "done" && s.ts !== null && s.endTs !== null && s.endTs > s.ts) step.duration = dur(s.endTs - s.ts);
    if (s.status === "done" && s.state === "collecting" && s.reason) step.note = s.reason;
    if (s.status === "stopped") {
      const why = s.reason ?? (s.state === "cancelled" ? "cancelled" : "failed");
      step.why = facts.atT !== null && !s.fromRecord ? `${why} · ${rel(Math.floor(facts.atT / 1000) * 1000, 0)}` : why;
    }
    return step;
  });
  const fromRecord = stream.snap.events.length === 0;
  return (
    <div className={className}>
      <p className="label mb-3">
        Lifecycle{" "}
        <span className="faint normal-case tracking-normal">
          · UTC{fromRecord ? " · from the job record" : ""}
          {stream.t0Source === "booting" && !stream.live && " · T0 = VM boot · sample never started"}
        </span>
      </p>
      {/* narrow columns: the T0 tag and the live pip wrap under the label instead of clipping */}
      <Stepper steps={out} className="[&_.step-l]:flex-wrap [&_.step-l]:gap-y-1" />
    </div>
  );
}

// ---- actions ------------------------------------------------------------------------------

/** Hand the viewer a file built in the page (exports, a saved frame). */
export function saveBlob(name: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

const csvCell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

function iocList(ctx: ReportCtx): IocEntry[] {
  const fromReport = ctx.behaviorReport?.iocs;
  if (fromReport) return Object.values(fromReport).flatMap((l) => l ?? []);
  return [...ctx.stream.snap.iocs.values()].map((i) => ({ kind: i.kind, value: i.value, hits: i.hits, sources: i.sources }));
}

export function baseName(job: Job) {
  return (jobName(job).replace(/[^\w.-]+/g, "_").replace(/^_+|_+$/g, "") || job.id).slice(0, 80);
}

function ExportIocs({ ctx }: { ctx: ReportCtx }) {
  const list = iocList(ctx);
  if (!list.length) return null;
  const base = baseName(ctx.job);
  const json = () => {
    const body = ctx.behaviorReport?.iocs ?? list.reduce<Record<string, IocEntry[]>>((m, e) => ((m[e.kind] ??= []).push(e), m), {});
    saveBlob(`${base}-iocs.json`, new Blob([JSON.stringify(body, null, 2)], { type: "application/json" }));
  };
  const csv = () => {
    const rows = list.map((e) => {
      const sources = e.sources ?? [];
      const isStatic = sources.length > 0 && sources.every((s) => s.startsWith("static"));
      const bg = backgroundIoc(e.kind, e.value, ctx.noise) ?? "";
      return [e.kind, e.value, String(e.hits), sources.join(";"), String(isStatic), bg].map(csvCell).join(",");
    });
    saveBlob(`${base}-iocs.csv`, new Blob([["kind,value,hits,seen_in,static,background", ...rows].join("\n") + "\n"], { type: "text/csv" }));
  };
  return (
    <div className="inline-flex" role="group" aria-label={`Export ${count(list.length)} indicators`}>
      <button type="button" className={buttonClass({ variant: "secondary" })} onClick={json} aria-label="Export indicators as JSON">
        Export IOCs <span className="font-normal text-[11px]">JSON</span>
      </button>
      <button type="button" className={buttonClass({ variant: "secondary", className: "-ml-px px-3.5 font-normal" })} onClick={csv} aria-label="Export indicators as CSV">
        CSV
      </button>
    </div>
  );
}

function DownloadReport({ ctx }: { ctx: ReportCtx }) {
  const { download, pending } = usePresignedDownload();
  const report = pickArtifact(ctx.artifacts, "behavior-report");
  const base = baseName(ctx.job);
  if (!report) {
    return (
      <a className={buttonClass({ variant: "primary" })} href={api.reportURL(ctx.job.id)} download={`${base}-report.html`}>
        Download report <span className="font-normal text-[11px]">HTML</span>
      </a>
    );
  }
  const busy = pending === report.sha256;
  return (
    <button
      type="button"
      className={buttonClass({ variant: "primary" })}
      aria-busy={busy || undefined}
      onClick={() => void download(ctx.job.id, { sha256: report.sha256, name: `${base}-behavior-report.json` })}
    >
      {busy ? <span className="breathe">Preparing…</span> : "Download report"} <span className="font-normal text-[11px]">JSON</span>
    </button>
  );
}

export function scrollToFig(no: number) {
  scrollToId(`fig-${no}`);
}

export function DocumentActions({ ctx, plan }: { ctx: ReportCtx; plan?: DocumentPlan }) {
  const artifactsFig = plan?.figs.find((f) => f.section === "artifacts");
  return (
    <div className="mt-6 flex flex-wrap items-center gap-2.5">
      <DownloadReport ctx={ctx} />
      <RerunButton ctx={ctx} />
      <ExportIocs ctx={ctx} />
      {artifactsFig && ctx.artifacts.length > 0 && (
        <button type="button" className={buttonClass({ variant: "secondary" })} onClick={() => scrollToFig(artifactsFig.no)}>
          Artifacts <span className="font-normal tabular-nums">{count(ctx.artifacts.length)}</span>
        </button>
      )}
      <a className={buttonClass({ variant: "ghost" })} href={api.reportURL(ctx.job.id)} target="_blank" rel="noopener">
        Open printable report <Arrow />
      </a>
    </div>
  );
}

/** Submit a new job backed by the same already-uploaded sample. The
 *  bytes are in S3 (keyed by sha); the backend's /v1/jobs/finalize
 *  endpoint creates a new row referencing the existing object — one
 *  POST, no upload. Reuses the current job's profile/network/timeout
 *  so the common 'did this behave the same way twice' use-case is one
 *  click. If a Turnstile check is required and we have no token, we
 *  redirect to Submit prefilled instead of failing silently. */
function RerunButton({ ctx }: { ctx: ReportCtx }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const { job } = ctx;
  const canRerun = job.kind === "file" && !!job.sample_sha;
  if (!canRerun) return null;
  const rerun = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const next = await api.rerunSample(job.sample_sha!, job.filename ?? "sample", {
        profile: job.profile,
        network: job.network,
        timeout_secs: job.timeout_secs,
        command: job.command,
        allow_input: job.allow_input,
        auto_analyze_children: job.auto_analyze_children,
        parent_job_id: job.id,
      });
      navigate(`/jobs/${next.id}`, { viewTransition: true });
    } catch (e) {
      // Turnstile / auth / quota failures land here. Redirect to the
      // Submit page with the sample context so the user can retry
      // through the normal arm flow (turnstile widget + sign-in CTA).
      const q = new URLSearchParams({ rerun: job.sample_sha!, name: job.filename ?? "", profile: job.profile ?? "", network: job.network ?? "", timeout: String(job.timeout_secs ?? "") });
      navigate(`/?${q}`);
      // eslint-disable-next-line no-console
      console.warn("rerun fell back to Submit page:", e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      className={buttonClass({ variant: "secondary" })}
      onClick={rerun}
      aria-busy={busy || undefined}
      title="Submit a new job backed by the same sample bytes (no re-upload)"
    >
      {busy ? <span className="breathe">Submitting…</span> : "Rerun"}
    </button>
  );
}

// ---- header ---------------------------------------------------------------------------------

/** A finished run's event stream that cannot connect, before anything arrived: say so instead of an empty report. */
function StreamTrouble({ ctx }: { ctx: ReportCtx }) {
  const { stream, job } = ctx;
  const down = !!job.node_id && !stream.ended && stream.snap.events.length === 0 && (stream.connection === "reconnecting" || stream.connection === "closed");
  const now = useClock(1000, down && !!stream.retry);
  if (!down) return null;
  const retryIn = stream.retry ? Math.max(0, stream.retry.at - now) : null;
  return (
    <Alert
      head="Couldn't load the event stream"
      className="mt-4 mb-1"
      actions={
        <button type="button" className="lnk" onClick={stream.reconnect}>
          Retry now
        </button>
      }
    >
      {retryIn !== null ? `retrying in ${secs(retryIn + 999)}` : "stopped retrying"} · processes, network and events not loaded
    </Alert>
  );
}

const PHONE = "(max-width: 720px)";
const onPhoneChange = (cb: () => void) => {
  const m = window.matchMedia(PHONE);
  m.addEventListener("change", cb);
  return () => m.removeEventListener("change", cb);
};
/** Phones get a shorter threat-tag row (three, then "+N"). */
function usePhone(): boolean {
  return useSyncExternalStore(onPhoneChange, () => window.matchMedia(PHONE).matches, () => false);
}

/** The status chip beside the headline: the verdict banner of a document (dashed "partial" unless it completed),
 *  "not scored", QUEUED, or the ink stop chip of a failed run or one cancelled before dispatch. */
function StatusChip({ ctx, variant }: { ctx: ReportCtx; variant: Variant }) {
  if (variant === "queued") return <QueuedBadge size="lg" title="Waiting for a free slot on a node that offers this profile" />;
  if (variant === "failed" || variant === "cancelled-before")
    return (
      <span className="stop-badge" title={ctx.stream.reason ?? ctx.job.reason ?? undefined}>
        <span className="x" aria-hidden="true">
          ×
        </span>
        {variant === "failed" ? "failed" : "cancelled"}
      </span>
    );
  if (variant !== "document") return null;
  const v = ctx.stream.verdict;
  if (!v) return <VerdictBanner v="pending" label="not scored" title="The run stopped before it was scored" />;
  const partial = ctx.stream.state !== "completed";
  return (
    <VerdictBanner
      v={v.label}
      score={v.score}
      of
      partial={partial}
      title={partial ? `Partial: scored ${v.score} of 100 on what the run recorded before it stopped` : `${v.label}, scored ${v.score} of 100`}
    />
  );
}

export function ReportHeader({
  ctx,
  variant,
  facts,
  lens,
  actions,
  identity = true,
}: {
  ctx: ReportCtx;
  variant: Variant;
  facts: ReportFacts;
  lens: ReactNode;
  actions?: ReactNode;
  identity?: boolean;
}) {
  const { job, stream } = ctx;
  const name = jobName(job);
  const phone = usePhone();
  const stopped = !stream.live && !stream.ended && stream.snap.events.length > 0 && (stream.state === "cancelled" || stream.state === "failed");
  const neverStarted = stream.state !== "completed" && !facts.sampleStarted;
  const threats = useMemo(() => {
    if (variant !== "document" || !stream.verdict || neverStarted) return [];
    const v = reportVerdict(ctx.stream, ctx.behaviorReport);
    return threatItems(v?.tags ?? [], facts.yara.map((y) => y.rule), facts.config.families);
  }, [variant, ctx, stream.verdict, facts.yara, facts.config.families, neverStarted]);
  return (
    <div className="rhead" id="top">
      <div className="rhead-id">
        <Kicker dash>
          Analysis <Kid>{job.id}</Kid>
          <Sep />
          <StateWord stream={stream} />
          {stream.recordLags && (
            <>
              <Sep />
              <span className={NOTE}>record syncing</span>
            </>
          )}
          {stopped && (
            <>
              <Sep />
              <span className={NOTE}>
                {stream.connection === "closed" ? `stream stopped at seq ${count(stream.lastSeq)}` : `replayed to seq ${count(stream.lastSeq)}`}
              </span>
            </>
          )}
        </Kicker>
        <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-3">
          {/* two lines at most: a 180-character name must not push the report down; the title and copy hold all of it */}
          <h1 className="headline line-clamp-2 min-w-0" translate="no" title={name}>
            {name}
          </h1>
          {name.length > LONG_NAME && <CopyButton value={name} label="sample name" className="-ml-3" />}
          <StatusChip ctx={ctx} variant={variant} />
        </div>
        {threats.length > 0 && <ThreatTags className="mt-4" items={threats} max={phone ? 3 : 6} label="Threat tags: YARA rules, techniques and behaviours by severity" />}
        {job.parent_job_id && <ParentLink parentId={job.parent_job_id} />}
      </div>
      <div className="rhead-body">
        <StreamTrouble ctx={ctx} />
        {actions}
        <Lifecycle ctx={ctx} facts={facts} className="mt-8" />
        {identity && (
          <div className="mt-8">
            <IdentitySheets job={job} staticReport={ctx.staticReport} />
          </div>
        )}
        <ChildJobs job={job} />
      </div>
      {lens}
    </div>
  );
}
